import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Contact, LandingPageOption, LandingPageOptions } from '@app/modules/personas/interfaces/persona-landing-option.interface'
import { ToastrService } from '@app/services/toastr.service'
import { environment } from '@environments/environment'
import { Subject, Observable, EMPTY } from 'rxjs'
import { first } from 'rxjs/operators'
import { Persona } from '@app/modules/personas/interfaces/persona.interface'

@Injectable({
  providedIn: 'root'
})
export class EventLandingService {
  landingOptions$: Subject<LandingPageOptions> = new Subject<LandingPageOptions>()
  landingOptionChanges$: Subject<true> = new Subject<true>()
  changeBreakpoint: Subject<string> = new Subject<string>()
  private contacts: Contact
  private currentOptions: LandingPageOptions
  private currentPersona: Persona
  private originalDescription: string
  private isInitialLoad = true

  constructor(private http: HttpClient, private toastr: ToastrService) {}

  /**
   * Updates landing_page_options inside event object
   * @param persona_id number
   * @param options LandingPageOptions[]
   * @returns Observable
   */
  private updateOptions(persona_id: number, options: LandingPageOptions | { form: LandingPageOption }): Observable<any> {
    return this.http.patch(`${environment.planningApi}forms`, {
      id: persona_id,
      landing_page_options: options
    })
  }

  /**
   * Upload a file or set of files to database and get file status response
   */
  uploadFiles(workspaceSlug: string, event_slug: string, name: string, file: File, display_name: string): Observable<any> {
    const fileUploaded = new FormData()
    fileUploaded.append('workspace', workspaceSlug)
    fileUploaded.append('event_slug', event_slug)
    fileUploaded.append('files_upload[0][name]', name)
    fileUploaded.append('files_upload[0][display_name]', display_name)
    fileUploaded.append('files_upload[0][file]', file)
    return this.http.post(`${environment.mediaLibrary}event/upload`, fileUploaded, { reportProgress: true, observe: 'events' })
  }

  /**
   * Delete a file from library
   */
  deleteFile(workspaceSlug: string, id: number): Observable<any> {
    return this.http.post(`${environment.mediaLibrary}delete`, { file_id: id, workspace: workspaceSlug })
  }

  /**
   * Update file
   */
  updateFile(workspaceSlug: string, file_id: number, display_name: string): Observable<any> {
    const body = {
      workspace: workspaceSlug,
      file_id,
      display_name
    }
    return this.http.post(`${environment.mediaLibrary}update`, body)
  }

  /**
   * Publish landing page changes
   * @param persona_id number
   * @returns Observable
   */
  public publishLandingPage(persona_id: number): Observable<any> {
    const hasDescriptionChanges = this.currentPersona?.public_description !== this.originalDescription
    // Check if there are any changes in options or description
    if (!this.currentOptions && !hasDescriptionChanges) {
      this.toastr.error('Error', 'No changes to publish')
      return EMPTY
    }
    return this.updateOptions(persona_id, this.currentOptions).pipe(first())
  }

  /**
   * Get contacts list for a persona
   * @param value Contact
   */
  set contactsList(value: Contact) {
    this.contacts = value
  }

  /**
   * Get contacts list for a persona
   * @returns Contact
   */
  get contactsList(): Contact {
    return this.contacts
  }

  /**
   * Update landing page options locally
   * @param landingOptions LandingPageOptions
   */
  updateLandingOptionsLocally(landingOptions: LandingPageOptions): void {
    this.currentOptions = landingOptions
  }

  /**
   * Update current persona
   * @param persona Persona
   */
  updateCurrentPersona(persona: Persona): void {
    this.currentPersona = persona

    // Only store the original description on initial load
    if (this.isInitialLoad) {
      this.originalDescription = persona.public_description
      this.isInitialLoad = false
    }
  }
}
